import type { AgentProgressEvent, AgentTimelineEntry } from '~/components/AiChat/AgentTimeline/types';
import { calculateEventsDuration } from '~/components/AiChat/utils/timelineUtils';
import { foldProgressEvents, resolveRowStatus } from '~/components/ProgressTimeline/progressTimelineUtils';
import type { ProgressEventHandlers, ProgressStatus } from '~/components/ProgressTimeline/types';
import { PROGRESS_EVENT_KIND, PROGRESS_ROW_STATUS } from '~/components/ProgressTimeline/types';

import type {
  DocumentEventHandler,
  DocumentProgress,
  DocumentProgressMetadata,
  DocumentProgressRow,
  DocumentRunState,
} from './documentProgressTypes';
import { metaOf, progressKeyOf } from './documentProgressUtils';

const TEXTS = {
  PROCESSING: 'Processing document',
  COMPLETED: 'Successfully completed document processing and analyzing.',
  FAILED: 'Failed',
  // Names the failed row in the header when the failure carried no step.
  UNKNOWN_STEP: 'processing',
} as const;

const initRunState = (plan: AgentProgressEvent): DocumentRunState => ({
  rows: (metaOf(plan).steps ?? []).map((s) => ({
    key: s.key,
    title: s.title,
    index: s.index,
    status: PROGRESS_ROW_STATUS.PENDING,
  })),
  activeIndex: -1,
  text: plan.message || TEXTS.PROCESSING,
  isRunning: true,
  isFailed: false,
  failedStepKeys: new Set(),
});

/** The event's row, added in index order when it is a conditional step the plan left out. */
const ensureRow = (
  state: DocumentRunState,
  meta: DocumentProgressMetadata,
  title: string
): DocumentProgressRow | undefined => {
  if (!meta.step_key || meta.index === undefined) return undefined;
  const existing = state.rows.find((r) => r.key === meta.step_key);
  if (existing) return existing;
  const row: DocumentProgressRow = {
    key: meta.step_key,
    title,
    index: meta.index,
    status: PROGRESS_ROW_STATUS.PENDING,
  };
  state.rows.push(row);
  state.rows.sort((a, b) => a.index - b.index);
  return row;
};

const applyStepProgress: DocumentEventHandler = (state, event, meta) => {
  // The run ends when the store reports to CMS; a straggler after that changes nothing.
  if (!state.isRunning) return;
  const row = ensureRow(state, meta, event.message || meta.step_key || '');
  if (row && row.index >= state.activeIndex) {
    state.activeIndex = row.index;
    state.text = event.message || row.title;
    state.pages =
      meta.kind === PROGRESS_EVENT_KIND.STEP_PROGRESS ? { done: meta.done ?? 0, total: meta.total ?? 0 } : undefined;
    // OCR counts pages twice, labeling then reading, so a bare "4 / 4" then "1 / 4" looks like it went
    // backwards; the producer's message names the pass ("Labeled 4 of 4 pages", "Read 1 of 4 pages").
    if (state.pages) row.detail = event.message || `${state.pages.done} / ${state.pages.total}`;
  }
};

const applyStepFailed: DocumentEventHandler = (state, _event, meta) => {
  const row = ensureRow(state, meta, meta.step_key || '');
  if (!row) return;
  row.detail = meta.detail;
  state.failedStepKeys.add(row.key);
};

const applyCompleted: DocumentEventHandler = (state) => {
  state.isRunning = false;
  state.text = TEXTS.COMPLETED;
  state.pages = undefined;
};

const applyFailed: DocumentEventHandler = (state, _event, meta) => {
  state.isRunning = false;
  state.isFailed = true;
  state.text = TEXTS.FAILED;
  state.failureDetail = meta.detail;
  const row = state.rows.find((r) => r.key === meta.step_key);
  if (row) state.activeIndex = row.index;
};

const HANDLERS: ProgressEventHandlers<DocumentRunState, DocumentProgressMetadata> = {
  [PROGRESS_EVENT_KIND.STEP_STARTED]: applyStepProgress,
  [PROGRESS_EVENT_KIND.STEP_PROGRESS]: applyStepProgress,
  [PROGRESS_EVENT_KIND.STEP_FAILED]: applyStepFailed,
  [PROGRESS_EVENT_KIND.COMPLETED]: applyCompleted,
  [PROGRESS_EVENT_KIND.FAILED]: applyFailed,
};

const toDocumentProgress = (state: DocumentRunState, run: AgentProgressEvent[]): DocumentProgress => {
  const { rows, text, pages, isRunning, isFailed } = state;
  // Before any step_started the plan's first row is the one running.
  const activeIndex = state.activeIndex === -1 && rows.length ? rows[0].index : state.activeIndex;
  const activeRow = rows.find((r) => r.index === activeIndex);
  if (isFailed && activeRow) activeRow.detail = state.failureDetail ?? activeRow.detail;

  const isDone = !isRunning && !isFailed;
  rows.forEach((row) => {
    row.status = state.failedStepKeys.has(row.key)
      ? PROGRESS_ROW_STATUS.ERROR
      : resolveRowStatus({ position: row.index, activeIndex, isDone, failedIndex: isFailed ? activeIndex : undefined });
  });

  const entries: AgentTimelineEntry[] = rows.map((row) => ({
    id: row.key,
    title: row.title,
    content: row.detail,
    status: row.status,
  }));
  const status: ProgressStatus = {
    // The header names the step; the real error sits under that row as its detail.
    text: isFailed ? `${TEXTS.FAILED}: ${activeRow?.title ?? TEXTS.UNKNOWN_STEP}` : text,
    startedAt: run[0].timestamp,
    durationSeconds: isRunning ? null : calculateEventsDuration(run),
    isRunning,
  };
  return { rows, text, pages, isRunning, isFailed, entries, status };
};

/**
 * Reduces one document's events (from its latest `plan` on) to checklist rows.
 *
 * The plan holds only the steps every document runs; a conditional step (translation) gets its row
 * on its first `step_started`, in index order.
 */
const reduceDocument = (events: AgentProgressEvent[]): DocumentProgress | undefined => {
  const lastPlanAt = events.map((e) => metaOf(e).kind).lastIndexOf(PROGRESS_EVENT_KIND.PLAN);
  if (lastPlanAt === -1) return undefined;

  const run = events.slice(lastPlanAt);
  const [plan, ...later] = run;
  return toDocumentProgress(foldProgressEvents(later, initRunState(plan), HANDLERS), run);
};

/**
 * Groups a claim's `document_progress` events by document and reduces each to its rows.
 *
 * A long run emits an event per page, so its `plan` can leave the newest-event window while the run
 * is still going; `latestPlans` supplies it for a document whose events in the window carry none.
 */
export const toDocumentProgressByKey = (
  events: AgentProgressEvent[],
  latestPlans: Map<string, AgentProgressEvent> = new Map()
): Map<string, DocumentProgress> => {
  const byKey = new Map<string, AgentProgressEvent[]>();
  [...events]
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp) || a.event_id.localeCompare(b.event_id))
    .forEach((event) => {
      const key = progressKeyOf(event);
      if (!key) return;
      byKey.set(key, [...(byKey.get(key) ?? []), event]);
    });

  const result = new Map<string, DocumentProgress>();
  byKey.forEach((docEvents, key) => {
    const latestPlan = latestPlans.get(key);
    const hasPlan = docEvents.some((event) => metaOf(event).kind === PROGRESS_EVENT_KIND.PLAN);
    const progress = reduceDocument(!hasPlan && latestPlan ? [latestPlan, ...docEvents] : docEvents);
    if (progress) result.set(key, progress);
  });
  return result;
};
